import { PackageUnitTypeEnum } from "@/__generated__/graphql";
import { ServiceRadioCard } from "@/components/shared/booking/service-radio-card";
import { Clock, FlowerLotus } from "@/components/shared/icons";
import { ListHeading } from "@/components/shared/list-header";
import { ServiceRadioItem } from "@/components/shared/service-radio-item";
import { Typography } from "@/components/shared/typography";
import { RadioGroup, View } from "@/components/ui";
import {
  BodyMassageTypes,
  MessageAddOnCode,
  massageDuration,
} from "@/constants/booking";
import { formatPrice } from "@/lib/helpers/number";
import { useBookingState } from "@/store/booking/useBookingState";
import { useEffect, useMemo, useState } from "react";
import FootMassage from "@/assets/images/booking/foot-massage.png";
import { Divider } from "@/components/shared/divider";
import { ServiceCreditTopUpCard } from "../components/service-credit-topup-card";

export const HomeMassageBooking = () => {
  const [kw, setKw] = useState(BodyMassageTypes[0].title);
  const [duration, setDuration] = useState(massageDuration[0]);
  const [addOnFlag, setAddOnFlag] = useState(false);
  const {
    data: { packageDetails, packageDetailsFilter },
    setBookingState,
  } = useBookingState();

  const isBodyMassage = useMemo(() => {
    return (
      packageDetailsFilter?.unitTypes?.[0] === PackageUnitTypeEnum.BodyMassage
    );
  }, [packageDetailsFilter?.unitTypes?.[0]]);

  const addOnPackage = useMemo(() => {
    return packageDetails?.find((pkg) => pkg.code === MessageAddOnCode) || null;
  }, [packageDetails]);

  const durationPackage = useMemo(() => {
    return packageDetails.filter((pkg) => {
      if (
        pkg.unitType === packageDetailsFilter?.unitTypes?.[0] &&
        !pkg.code.includes("AO")
      ) {
        if (pkg.unitType === PackageUnitTypeEnum.FootMassage) return true;
        const bodyPkg = BodyMassageTypes?.find((type) => type.title === kw);
        if (bodyPkg?.codes?.includes(pkg.code)) return true;
        return false;
      }
    });
  }, [packageDetails, kw]);

  useEffect(() => {
    if (!isBodyMassage) setAddOnFlag(false);
  }, [isBodyMassage]);

  useEffect(() => {
    const durationPkgIds = durationPackage
      .filter((pkg) => {
        return pkg.duration === duration;
      })
      .map((pkg) => pkg.id);
    const addOnPkgId =
      packageDetails.find((pkg) => pkg.code === MessageAddOnCode)?.id || "";
    setBookingState({
      selectedPackageIds: addOnFlag
        ? [...durationPkgIds, addOnPkgId]
        : durationPkgIds,
    });
  }, [kw, duration, addOnFlag]);

  return (
    <View className="mt-2 flex flex-col gap-4">
      <View className="mx-4 mt-2 flex flex-col gap-2">
        <ServiceCreditTopUpCard service="massage" />
      </View>

      <Divider className="border-b-2" />

      {isBodyMassage && (
        <>
          <View className="mx-4 flex flex-col gap-4">
            <ListHeading title="Body Massage Type" icon={FlowerLotus} />
            <Typography color="foreground-intermediate" variant="body-sm">
              Select the massage style that suits your relaxation needs
            </Typography>
            <View className="flex flex-col gap-4">
              <RadioGroup value={kw} onValueChange={() => {}}>
                {BodyMassageTypes.map((pkg) => {
                  return (
                    <ServiceRadioCard
                      key={pkg.title}
                      title={pkg.title}
                      subTitle={pkg.description}
                      imgUrl={pkg.imgUrl}
                      value={pkg.title}
                      active={kw === pkg.title}
                      onSelect={() => setKw(pkg.title)}
                    />
                  );
                })}
              </RadioGroup>
            </View>
          </View>
          <Divider className="my-2 border-b-2" />
        </>
      )}

      <View className="mx-4 flex flex-col gap-2">
        <ListHeading
          title={`${isBodyMassage ? "Body" : "Foot"} ${"Massage Duration"}`}
          icon={Clock}
        />
        <Typography color="foreground-intermediate" variant="body-sm">
          Choose the perfect length for your massage experience.
        </Typography>
        <RadioGroup
          value={String(duration)}
          onValueChange={(value: string) => setDuration(Number(value))}
        >
          {massageDuration.map((d) => {
            const pkg = durationPackage.find((p) => p.duration === d);
            return (
              <ServiceRadioItem
                key={d}
                title={`${d * 60} Minutes`}
                subTitle={formatPrice(pkg?.unitValue || 0)}
                selected={d === duration}
                value={String(d)}
                onSelect={() => setDuration(d)}
              />
            );
          })}
        </RadioGroup>
      </View>

      {isBodyMassage && (
        <>
          <Divider className="my-2 border-b-2" />

          <View className="mx-4 flex flex-col gap-4">
            <ListHeading title="Add-On" icon={Clock} />
            <Typography color="foreground-intermediate" variant="body-sm">
              Choose additional treatments to complement your body massage for a
              more rejuvenating experience.
            </Typography>
            <RadioGroup value={String(addOnFlag)} onValueChange={() => {}}>
              <ServiceRadioCard
                active={addOnFlag}
                imgUrl={FootMassage}
                title="Foot Massage"
                subTitle={formatPrice(addOnPackage?.unitValue || 0)}
                thirdTitle="30 mins"
                value="true"
                onSelect={() => {
                  setAddOnFlag(!addOnFlag);
                }}
              />
            </RadioGroup>
          </View>

          <Divider className="my-2 border-b-2" />
        </>
      )}
    </View>
  );
};
